// ── The page cart ─────────────────────────────────────────────────────
//
// A basket a visitor can build on a PUBLISHED page, with no session and no
// server. Until this existed a storefront had `payment_checkout` — which
// takes money for a record the application already holds — and the Vision
// eCommerce blocks, which need an ERP source and render nothing on a static
// site. Neither is a cart: there was no way to put something in one.
//
// WHAT THIS IS NOT. It is a BASKET, never a price authority. The numbers
// here are for the visitor's benefit; the order is created through the
// transaction engine and `app_payments/checkout.py` re-reads every amount
// from the saved row, exactly as it does today. Nothing in localStorage is
// ever trusted with what something costs.
//
// ONE implementation, two callers, like the filter bar: the React surfaces
// import these functions, the static site gets the same ones serialised by
// `cartScript()`. Everything below the store boundary must therefore stay
// closure-free and dependency-free — `Function.prototype.toString` is what
// ships it.

export interface CartLine {
  sku: string
  name: string
  /** Numeric unit price, for arithmetic. */
  price: number
  /** What precedes the number when shown — '₹', '$', '€'. */
  prefix: string
  image?: string
  href?: string
  meta?: string
  qty: number
  // ── Booking lines (a date range: hotel stay, rental) ──
  /** First date, ISO yyyy-mm-dd. */
  start?: string
  /** Check-out (`night`) or last day used (`day`), ISO yyyy-mm-dd. */
  end?: string
  /** Nights or days in the range; the line total is price × qty × units. */
  units?: number
  /** What `price` is per — 'night' | 'day'. */
  unit?: string
  /** Line identity: the sku, plus the dates for a booking, so two stays in
   *  the same room type are two lines rather than one merged quantity. */
  key?: string
}

/** The identity a line is merged, stepped and removed by. */
export function cartLineKey(l: { sku: string; start?: string; end?: string; key?: string }): string {
  if (l.key) return String(l.key)
  return l.start ? String(l.sku) + '|' + l.start + '|' + (l.end || '') : String(l.sku)
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

/** Rules a `cart_summary` applies. Declared on the block: a static page
 *  cannot ask a server what tax or postage costs. */
export interface CartTotalsRules {
  taxPercent?: number
  taxLabel?: string
  shippingFlat?: number
  shippingFreeOver?: number
  shippingLabel?: string
  freeLabel?: string
}

export interface CartTotals {
  count: number
  subtotal: number
  tax: number
  shipping: number
  total: number
  prefix: string
}

// ── store ──────────────────────────────────────────────────────────────

export function cartKey(): string {
  // Per-origin, and per-site when a host serves several. A published site
  // has its own origin; the in-app viewer does not, so the scope global
  // keeps two projects' carts apart in one browser.
  const scope = (typeof window !== 'undefined'
    && (window as unknown as { __PG_CART_SCOPE__?: string }).__PG_CART_SCOPE__) || ''
  return 'pg_cart' + (scope ? ':' + scope : '')
}

/**
 * Declare which cart this surface is looking at.
 *
 * The scope global above was READ in three places and written in none, so
 * every in-app project shared one `pg_cart` and a basket built in one
 * application showed up in the next. A published site has an origin of its
 * own and passes nothing, which is what keeps its key the bare `pg_cart`
 * the static script already uses.
 *
 * Setting it is deliberately one-way: a chrome band or an embedded page
 * that does not know the project must not be able to CLEAR the scope the
 * page around it set, or the two would read different keys.
 */
export function setCartScope(scope: string | undefined): void {
  if (typeof window === 'undefined' || !scope) return
  const w = window as unknown as { __PG_CART_SCOPE__?: string }
  if (w.__PG_CART_SCOPE__ === scope) return
  w.__PG_CART_SCOPE__ = scope
  // A block that rendered before the scope was set read the OTHER key; tell
  // every reader to look again, or it shows one basket and submits another.
  try {
    window.dispatchEvent(new CustomEvent('pg:cart', { detail: { lines: readCart() } }))
  } catch { /* older browsers */ }
}

export function readCart(): CartLine[] {
  try {
    const raw = window.localStorage.getItem(cartKey())
    const parsed = raw ? JSON.parse(raw) : []
    if (!Array.isArray(parsed)) return []
    // Defend every field: this is browser storage, which a visitor can
    // edit, another tab can half-write, and a previous version can have
    // left in an older shape.
    const out: CartLine[] = []
    for (let i = 0; i < parsed.length; i++) {
      const l = parsed[i]
      if (!l || typeof l !== 'object' || !l.sku) continue
      const price = Number(l.price)
      const qty = Math.max(1, Math.min(Math.floor(Number(l.qty) || 1), 9999))
      const line: CartLine = {
        sku: String(l.sku), name: String(l.name || l.sku),
        price: isFinite(price) && price >= 0 ? price : 0,
        prefix: String(l.prefix || ''),
        image: l.image ? String(l.image) : undefined,
        href: l.href ? String(l.href) : undefined,
        meta: l.meta ? String(l.meta) : undefined,
        qty,
      }
      // A booking line keeps its range only when both ends are real dates;
      // a half-written one degrades to a plain line rather than a NaN total.
      if (ISO_DAY.test(String(l.start || '')) && ISO_DAY.test(String(l.end || ''))) {
        line.start = String(l.start)
        line.end = String(l.end)
        line.units = Math.max(1, Math.min(Math.floor(Number(l.units) || 1), 3660))
        line.unit = l.unit ? String(l.unit) : 'night'
      }
      line.key = cartLineKey(line)
      out.push(line)
    }
    return out
  } catch {
    // Private mode, blocked storage, corrupt JSON — an empty cart, never
    // an exception that takes the page down with it.
    return []
  }
}

export function writeCart(lines: CartLine[]): void {
  try {
    window.localStorage.setItem(cartKey(), JSON.stringify(lines))
  } catch { /* storage unavailable: the cart lives for this page view only */ }
  try {
    window.dispatchEvent(new CustomEvent('pg:cart', { detail: { lines } }))
  } catch { /* older browsers */ }
}

export function addToCart(line: Omit<CartLine, 'qty'> & { qty?: number }): CartLine[] {
  const lines = readCart()
  const add = Math.max(1, Math.floor(Number(line.qty) || 1))
  const key = cartLineKey(line)
  let found = false
  for (let i = 0; i < lines.length; i++) {
    if (cartLineKey(lines[i]) === key) {
      lines[i].qty = Math.min(lines[i].qty + add, 9999)
      found = true
      break
    }
  }
  if (!found) {
    const next: CartLine = {
      sku: String(line.sku), name: String(line.name || line.sku),
      price: Number(line.price) || 0, prefix: String(line.prefix || ''),
      image: line.image, href: line.href, meta: line.meta, qty: add, key,
    }
    if (line.start && line.end) {
      next.start = line.start
      next.end = line.end
      next.units = Math.max(1, Math.floor(Number(line.units) || 1))
      next.unit = line.unit || 'night'
    }
    lines.push(next)
  }
  writeCart(lines)
  return lines
}

/** `key` is the line's identity (`cartLineKey`) — the bare sku for an
 *  ordinary line, so existing callers passing a sku are unchanged. */
export function setCartQty(key: string, qty: number): CartLine[] {
  const next = Math.floor(Number(qty) || 0)
  let lines = readCart()
  if (next <= 0) {
    lines = lines.filter((l) => cartLineKey(l) !== key)
  } else {
    for (let i = 0; i < lines.length; i++) {
      if (cartLineKey(lines[i]) === key) lines[i].qty = Math.min(next, 9999)
    }
  }
  writeCart(lines)
  return lines
}

export function removeCartLine(key: string): CartLine[] {
  const lines = readCart().filter((l) => cartLineKey(l) !== key)
  writeCart(lines)
  return lines
}

/** What one line costs: unit price × quantity × nights/days. */
export function cartLineTotal(l: CartLine): number {
  return (Number(l.price) || 0) * l.qty * (l.units && l.units > 0 ? l.units : 1)
}

/** Indian digit grouping when the prefix says rupees, plain grouping
 *  otherwise — 1,00,000 and 100,000 are both wrong in the other's place. */
export function formatMoney(n: number, prefix: string): string {
  const v = Math.round(Number(n) || 0)
  const s = String(Math.abs(v))
  let grouped: string
  if (prefix.indexOf('₹') !== -1) {
    if (s.length <= 3) grouped = s
    else {
      let head = s.slice(0, -3)
      const tail = s.slice(-3)
      const parts: string[] = []
      while (head.length > 2) { parts.unshift(head.slice(-2)); head = head.slice(0, -2) }
      if (head) parts.unshift(head)
      grouped = parts.join(',') + ',' + tail
    }
  } else {
    grouped = s.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }
  return (v < 0 ? '-' : '') + prefix + grouped
}

export function cartTotals(lines: CartLine[], rules: CartTotalsRules): CartTotals {
  let count = 0
  let subtotal = 0
  let prefix = ''
  for (let i = 0; i < lines.length; i++) {
    count += lines[i].qty
    subtotal += cartLineTotal(lines[i])
    if (!prefix && lines[i].prefix) prefix = lines[i].prefix
  }
  const tax = rules.taxPercent ? Math.round(subtotal * (rules.taxPercent / 100)) : 0
  let shipping = 0
  if (rules.shippingFlat) {
    const free = rules.shippingFreeOver
    shipping = (free !== undefined && free !== null && subtotal >= free) ? 0 : rules.shippingFlat
  }
  // An empty cart has no postage to pay.
  if (count === 0) shipping = 0
  return { count, subtotal, tax, shipping, total: subtotal + tax + shipping, prefix }
}

/**
 * The cart as a transaction DRAFT, for an embed's `recordVar` to seed from.
 *
 * `__details__` is keyed by child form_no, which is the same vocabulary the
 * embed already uses for its detail grids. Only the sku and the quantity
 * travel: everything else on the line — description, unit price, tax rate —
 * is filled by the line's own `item_change` from the product master when the
 * sku lands, so a basket a visitor has edited cannot change what is charged.
 *
 * A BOOKING line also carries its range when the source names the columns
 * (`startColumn` / `endColumn` / `unitsColumn`): the dates are the visitor's
 * choice, not a price, and the line's own logic prices `rate × units` and
 * checks availability on save.
 *
 * An empty cart yields an empty detail set rather than nothing, so clearing
 * the basket clears the draft instead of leaving the last one on the form.
 */
export function cartDraft(
  lines: CartLine[],
  src: {
    detailFormNo: string; skuColumn: string; qtyColumn: string
    startColumn?: string; endColumn?: string; unitsColumn?: string
  },
): Record<string, unknown> {
  const rows = []
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i]
    if (!l || !l.sku) continue
    const row: Record<string, unknown> = { [src.skuColumn]: l.sku, [src.qtyColumn]: l.qty }
    if (l.start && l.end) {
      if (src.startColumn) row[src.startColumn] = l.start
      if (src.endColumn) row[src.endColumn] = l.end
      if (src.unitsColumn) row[src.unitsColumn] = l.units || 1
    }
    rows.push(row)
  }
  return { __details__: { [String(src.detailFormNo)]: rows } }
}

// ── DOM wiring ─────────────────────────────────────────────────────────
//
// The blocks render a shell; this fills it. One implementation so a cart on
// a published page behaves exactly like the one in the designer — the
// React surfaces call `initCart` from an effect, the static site runs the
// same code from `cartScript()`.

export function initCart(): void {
  const doc = document

  const read = (): CartLine[] => {
    try {
      const scope = (window as unknown as { __PG_CART_SCOPE__?: string }).__PG_CART_SCOPE__ || ''
      const raw = window.localStorage.getItem('pg_cart' + (scope ? ':' + scope : ''))
      const parsed = raw ? JSON.parse(raw) : []
      return Array.isArray(parsed) ? parsed.filter((l: CartLine) => l && l.sku) : []
    } catch { return [] }
  }
  const write = (lines: CartLine[]) => {
    try {
      const scope = (window as unknown as { __PG_CART_SCOPE__?: string }).__PG_CART_SCOPE__ || ''
      window.localStorage.setItem('pg_cart' + (scope ? ':' + scope : ''), JSON.stringify(lines))
    } catch { /* storage blocked */ }
    try { window.dispatchEvent(new CustomEvent('pg:cart', { detail: { lines } })) } catch { /* old browser */ }
  }
  const money = (n: number, prefix: string): string => {
    const v = Math.round(Number(n) || 0)
    const s = String(Math.abs(v))
    let grouped: string
    if (prefix.indexOf('₹') !== -1) {
      if (s.length <= 3) grouped = s
      else {
        let head = s.slice(0, -3); const tail = s.slice(-3); const parts: string[] = []
        while (head.length > 2) { parts.unshift(head.slice(-2)); head = head.slice(0, -2) }
        if (head) parts.unshift(head)
        grouped = parts.join(',') + ',' + tail
      }
    } else {
      grouped = s.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
    return (v < 0 ? '-' : '') + prefix + grouped
  }
  const num = (el: Element | null, attr: string, dflt: number): number => {
    const raw = el && el.getAttribute(attr)
    const v = raw === null || raw === '' ? NaN : Number(raw)
    return isFinite(v) ? v : dflt
  }
  // Mirrors of `cartLineKey` / `cartLineTotal`: this function is shipped by
  // `toString()` and cannot reach anything at module scope.
  const keyOf = (l: Partial<CartLine>): string =>
    l.key ? String(l.key)
      : (l.start ? String(l.sku) + '|' + l.start + '|' + (l.end || '') : String(l.sku || ''))
  const lineTotal = (l: CartLine): number =>
    (Number(l.price) || 0) * (Number(l.qty) || 0) * (l.units && l.units > 0 ? l.units : 1)
  const plural = (n: number, unit: string): string => n + ' ' + unit + (n === 1 ? '' : 's')

  // ── dates ── whole days as UTC day numbers, so a clock change never moves
  // a night and "3 nights" is plain subtraction.
  const dayNo = (iso: string): number => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''))
    return m ? Math.floor(Date.UTC(+m[1], +m[2] - 1, +m[3]) / 86400000) : NaN
  }
  const isoOf = (n: number): string => new Date(n * 86400000).toISOString().slice(0, 10)
  const todayNo = (): number => {
    const d = new Date()
    return Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86400000)
  }
  const monthOf = (n: number): number => {
    const d = new Date(n * 86400000)
    return Math.floor(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1) / 86400000)
  }
  const fmtDay = (iso: string, long?: boolean): string => {
    const n = dayNo(iso)
    if (!isFinite(n)) return iso
    try {
      return new Date(n * 86400000).toLocaleDateString(undefined, long
        ? { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }
        : { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
    } catch { return iso }
  }

  interface BookCfg {
    unit: string; min: number; max: number; adv: number; blocked: string[]
    start: string; end: string; startLabel: string; endLabel: string
  }
  const cfgOf = (box: Element): BookCfg | null => {
    const raw = box.getAttribute('data-pg-book')
    if (!raw) return null
    try {
      const c = JSON.parse(raw) || {}
      const unit = c.unit === 'day' ? 'day' : 'night'
      return {
        unit,
        min: Math.max(1, Math.floor(Number(c.min) || 1)),
        max: Math.max(0, Math.floor(Number(c.max) || 0)),
        adv: Math.max(0, Math.floor(c.adv === undefined || c.adv === null ? 365 : Number(c.adv) || 0)),
        blocked: Array.isArray(c.blocked) ? c.blocked.map(String) : [],
        start: String(c.start || ''), end: String(c.end || ''),
        startLabel: String(c.startLabel || (unit === 'day' ? 'Pick-up' : 'Check-in')),
        endLabel: String(c.endLabel || (unit === 'day' ? 'Return' : 'Check-out')),
      }
    } catch { return null }
  }
  // A `night` range ends on the check-out day, which is free; a `day` range
  // ends on the last day used, which is not.
  const unitsOf = (cfg: BookCfg, s: number, e: number): number => (cfg.unit === 'day' ? e - s + 1 : e - s)
  const startProblem = (cfg: BookCfg, s: number): string => {
    const today = todayNo()
    if (!isFinite(s)) return 'Choose a ' + cfg.startLabel.toLowerCase() + ' date.'
    if (s < today) return 'That date has passed.'
    if (s > today + cfg.adv) return 'Bookings open ' + plural(cfg.adv, 'day') + ' ahead.'
    if (cfg.blocked.indexOf(isoOf(s)) !== -1) return fmtDay(isoOf(s), true) + ' is not available.'
    return ''
  }
  const rangeProblem = (cfg: BookCfg, s: number, e: number): string => {
    const first = startProblem(cfg, s)
    if (first) return first
    if (!isFinite(e)) return 'Choose a ' + cfg.endLabel.toLowerCase() + ' date.'
    const units = unitsOf(cfg, s, e)
    if (units < 1) return cfg.endLabel + ' must be after ' + cfg.startLabel.toLowerCase() + '.'
    if (units < cfg.min) return 'The minimum is ' + plural(cfg.min, cfg.unit) + '.'
    if (cfg.max && units > cfg.max) return 'The maximum is ' + plural(cfg.max, cfg.unit) + '.'
    const last = cfg.unit === 'day' ? e : e - 1
    for (let d = s; d <= last; d++) {
      if (cfg.blocked.indexOf(isoOf(d)) !== -1) return fmtDay(isoOf(d), true) + ' is not available.'
    }
    return ''
  }
  const say = (box: Element, text: string) => {
    const el = box.querySelector('[data-pg-book-msg]') as HTMLElement | null
    if (!el) return
    el.textContent = text
    el.style.display = text ? 'block' : 'none'
  }
  const pickedQty = (box: Element | null, dflt: number): number => {
    const input = box && box.querySelector('[data-pg-buy-qty]') as HTMLInputElement | null
    if (!input) return Math.max(1, Math.floor(Number(dflt) || 1))
    const max = Math.max(1, Math.floor(Number(input.getAttribute('max')) || 9999))
    const v = Math.max(1, Math.min(Math.floor(Number(input.value) || 1), max))
    if (String(v) !== input.value) input.value = String(v)
    return v
  }
  const el = (tag: string, css: string, text?: string): HTMLElement => {
    const n = doc.createElement(tag)
    n.style.cssText = css
    if (text !== undefined) n.textContent = text
    return n
  }

  // ── the booking calendar ── a range picker drawn with plain DOM so a
  // static page gets the same one. State lives on the wrapper as attributes
  // (start / end / month on view), so a re-render — or React re-rendering
  // the button around it — never loses the visitor's choice.
  const renderBooking = (box: HTMLElement) => {
    const cfg = cfgOf(box)
    if (!cfg) return
    const today = todayNo()
    if (!box.hasAttribute('data-pg-book-ready')) {
      box.setAttribute('data-pg-book-ready', '')
      const ds = dayNo(cfg.start)
      const de = dayNo(cfg.end)
      // A pre-selected range is honoured only when it is bookable as-is.
      if (isFinite(ds) && !startProblem(cfg, ds)) {
        box.setAttribute('data-pg-book-start', isoOf(ds))
        if (isFinite(de) && !rangeProblem(cfg, ds, de)) box.setAttribute('data-pg-book-end', isoOf(de))
      }
    }
    const s = dayNo(box.getAttribute('data-pg-book-start') || '')
    const e = dayNo(box.getAttribute('data-pg-book-end') || '')
    const lastDay = today + cfg.adv + (cfg.max || 60)
    if (!box.hasAttribute('data-pg-cal-month')) {
      box.setAttribute('data-pg-cal-month', String(monthOf(isFinite(s) ? s : today)))
    }
    const month = Number(box.getAttribute('data-pg-cal-month'))

    const host = box.querySelector('[data-pg-book-cal]') as HTMLElement | null
    if (host) {
      host.innerHTML = ''
      const first = new Date(month * 86400000)
      const y = first.getUTCFullYear()
      const mo = first.getUTCMonth()
      const daysIn = new Date(Date.UTC(y, mo + 1, 0)).getUTCDate()
      const lead = (first.getUTCDay() + 6) % 7 // weeks start on Monday

      const head = el('div', 'display:flex;align-items:center;justify-content:space-between;margin-bottom:6px')
      const nav = (delta: number, label: string, off: boolean) => {
        const b = el('button', 'width:30px;height:30px;border:1px solid var(--pg-border);border-radius:8px;background:none;color:var(--pg-text);cursor:pointer;font:inherit;font-size:16px;line-height:1' + (off ? ';opacity:.35;pointer-events:none' : ''), delta < 0 ? '‹' : '›')
        b.setAttribute('type', 'button')
        b.setAttribute('data-pg-cal-nav', String(delta))
        b.setAttribute('aria-label', label)
        if (off) b.setAttribute('disabled', '')
        return b
      }
      let title = y + '-' + String(mo + 1).padStart(2, '0')
      try { title = first.toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' }) } catch { /* keep ISO */ }
      head.appendChild(nav(-1, 'Previous month', month <= monthOf(today)))
      head.appendChild(el('strong', 'font-size:.95rem;color:var(--pg-heading, var(--pg-text))', title))
      head.appendChild(nav(1, 'Next month', monthOf(lastDay) <= month))
      host.appendChild(head)

      const grid = el('div', 'display:grid;grid-template-columns:repeat(7,1fr);gap:2px')
      grid.setAttribute('role', 'grid')
      const wd = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']
      for (let i = 0; i < 7; i++) {
        grid.appendChild(el('span', 'text-align:center;font-size:.7rem;font-weight:600;color:var(--pg-muted, var(--pg-text-secondary));padding:2px 0', wd[i]))
      }
      for (let i = 0; i < lead; i++) grid.appendChild(el('span', ''))
      for (let i = 0; i < daysIn; i++) {
        const d = month + i
        const iso = isoOf(d)
        const blocked = cfg.blocked.indexOf(iso) !== -1
        // A blocked day can still be the CHECK-OUT of a night stay — the
        // night that matters is the one before it.
        const asCheckout = cfg.unit === 'night' && isFinite(s) && !isFinite(e) && d > s
        const usable = d >= today && d <= lastDay && (!blocked || asCheckout)
        const isEdge = d === s || d === e
        const inRange = isFinite(s) && isFinite(e) && d > s && d < e
        let css = 'height:34px;border-radius:8px;font:inherit;font-size:.85rem;cursor:pointer;border:1px solid transparent;font-variant-numeric:tabular-nums;'
        if (isEdge) css += 'background:var(--pg-primary);color:var(--pg-on-primary, #fff);font-weight:700;'
        else if (inRange) css += 'background:color-mix(in srgb, var(--pg-primary) 14%, transparent);color:var(--pg-text);'
        else css += 'background:none;color:var(--pg-text);'
        if (d === today && !isEdge) css += 'border-color:var(--pg-border);'
        if (!usable) css += 'opacity:.35;cursor:not-allowed;' + (blocked ? 'text-decoration:line-through;' : '')
        const b = el('button', css, String(i + 1))
        b.setAttribute('type', 'button')
        b.setAttribute('data-pg-cal-day', iso)
        b.setAttribute('aria-label', fmtDay(iso, true) + (blocked ? ' — not available' : ''))
        if (isEdge) b.setAttribute('aria-pressed', 'true')
        if (!usable) b.setAttribute('disabled', '')
        grid.appendChild(b)
      }
      host.appendChild(grid)
    }

    // What has been chosen, and what it comes to.
    const sum = box.querySelector('[data-pg-book-summary]') as HTMLElement | null
    if (sum) {
      if (isFinite(s) && isFinite(e)) {
        const units = unitsOf(cfg, s, e)
        let text = cfg.startLabel + ' ' + fmtDay(isoOf(s)) + ' → ' + cfg.endLabel + ' ' + fmtDay(isoOf(e))
          + ' · ' + plural(units, cfg.unit)
        const btn = box.querySelector('[data-pg-add-cart]')
        try {
          const p = JSON.parse((btn && btn.getAttribute('data-pg-add-cart')) || '{}')
          const price = Number(p.price) || 0
          if (price) text += ' · ' + money(price * units * pickedQty(box, p.qty), String(p.prefix || ''))
        } catch { /* no price to show */ }
        sum.textContent = text
      } else if (isFinite(s)) {
        sum.textContent = cfg.startLabel + ' ' + fmtDay(isoOf(s)) + ' — now choose ' + cfg.endLabel.toLowerCase() + '.'
      } else {
        sum.textContent = 'Choose ' + cfg.startLabel.toLowerCase() + ' and ' + cfg.endLabel.toLowerCase() + ' dates.'
      }
    }
  }

  // ── an add button's in-cart state ── The button carries its own rules
  // as attributes (React writes them; the static page has the same
  // markup), and the controls it may swap to are HIDDEN TEMPLATES beside it
  // inside `[data-pg-cart-ctl]` — so nothing here builds markup, and a
  // published page with no React behaves exactly like the canvas.
  //   data-pg-in-cart-label  "In cart · {n}" (absent ⇒ feature off)
  //   [data-pg-in-cart-ui="stepper"]  replaces the button: − text +
  //   [data-pg-in-cart-ui="note"]     sits beside it: text only
  const paintAdd = (btn: HTMLElement, inCart: Record<string, number>) => {
    let sku = ''
    try { sku = String((JSON.parse(btn.getAttribute('data-pg-add-cart') || '{}') || {}).sku || '') } catch { /* no sku */ }
    const n = sku ? (inCart[sku] || 0) : 0
    if (n > 0) btn.setAttribute('data-pg-in-cart', String(n))
    else btn.removeAttribute('data-pg-in-cart')
    // Remember how the button was laid out before it is ever hidden:
    // clearing an inline `display` does not restore an inline-flex.
    if (!btn.hasAttribute('data-pg-display')) btn.setAttribute('data-pg-display', btn.style.display || '')
    const labelEl = btn.querySelector('[data-pg-cart-label]') as HTMLElement | null
    if (labelEl && !btn.hasAttribute('data-pg-label')) btn.setAttribute('data-pg-label', labelEl.textContent || '')
    const tmpl = btn.getAttribute('data-pg-in-cart-label')
    const ctl = btn.closest ? btn.closest('[data-pg-cart-ctl]') as HTMLElement | null : null
    const ui = ctl ? ctl.querySelector('[data-pg-in-cart-ui]') as HTMLElement | null : null
    const flashing = btn.hasAttribute('data-pg-added')
    const show = !!ui && tmpl !== null && tmpl !== '' && n > 0 && !flashing
    if (ui) {
      if (show) {
        ui.removeAttribute('hidden')
        ui.style.display = 'inline-flex'
        const text = ui.querySelector('[data-pg-in-cart-text]') as HTMLElement | null
        if (text) text.textContent = String(tmpl).replace(/\{n\}/g, String(n))
        const steps = ui.querySelectorAll('[data-pg-cart-step]')
        for (let k = 0; k < steps.length; k++) steps[k].setAttribute('data-pg-cart-sku', sku)
      } else {
        ui.setAttribute('hidden', '')
        ui.style.display = 'none'
      }
    }
    const replaced = show && ui!.getAttribute('data-pg-in-cart-ui') === 'stepper'
    btn.style.display = replaced ? 'none' : (btn.getAttribute('data-pg-display') || '')
    if (labelEl && !flashing) labelEl.textContent = btn.getAttribute('data-pg-label') || labelEl.textContent || ''
  }

  // ── "Added to cart · View cart" ── one notice per document, drawn with
  // plain DOM and coloured from the theme variables at the button, because
  // it lives on <body>, outside the themed page that defines them.
  const toast = (from: HTMLElement, cfg: { text?: string; link?: string; href?: string }) => {
    const w = window as unknown as { __pgCartToastT?: number }
    let box = doc.querySelector('[data-pg-cart-toast-el]') as HTMLElement | null
    if (!box) {
      box = el('div', 'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:2147483000;'
        + 'display:none;align-items:center;gap:14px;padding:12px 18px;border-radius:999px;'
        + 'box-shadow:0 10px 30px rgba(0,0,0,.22);font-size:15px;font-weight:600;max-width:calc(100vw - 32px)')
      box.setAttribute('data-pg-cart-toast-el', '')
      box.setAttribute('role', 'status')
      box.setAttribute('aria-live', 'polite')
      doc.body.appendChild(box)
    }
    let bg = ''; let fg = ''; let font = ''
    try {
      const cs = window.getComputedStyle(from)
      bg = cs.getPropertyValue('--pg-heading').trim() || cs.getPropertyValue('--pg-text').trim()
      fg = cs.getPropertyValue('--pg-bg').trim() || cs.getPropertyValue('--pg-surface').trim()
      font = cs.getPropertyValue('--pg-font').trim()
    } catch { /* defaults below */ }
    box.style.background = bg || '#1f2937'
    box.style.color = fg || '#ffffff'
    box.style.fontFamily = font || 'inherit'
    box.innerHTML = ''
    box.appendChild(el('span', '', String(cfg.text || 'Added to cart')))
    if (cfg.href) {
      const a = el('a', 'color:inherit;text-decoration:underline;font-weight:700;white-space:nowrap', String(cfg.link || 'View cart')) as HTMLAnchorElement
      a.href = cfg.href
      box.appendChild(a)
    }
    box.style.display = 'flex'
    if (w.__pgCartToastT) window.clearTimeout(w.__pgCartToastT)
    w.__pgCartToastT = window.setTimeout(() => { if (box) box.style.display = 'none' }, 3500)
  }

  const render = () => {
    const lines = read()
    let count = 0; let subtotal = 0; let prefix = ''
    for (let i = 0; i < lines.length; i++) {
      count += Number(lines[i].qty) || 0
      subtotal += lineTotal(lines[i])
      if (!prefix && lines[i].prefix) prefix = lines[i].prefix
    }

    // badges
    const badges = doc.querySelectorAll('[data-pg-cart-count]')
    for (let i = 0; i < badges.length; i++) {
      const b = badges[i] as HTMLElement
      b.textContent = String(count)
      if (b.hasAttribute('data-pg-cart-hide-empty')) {
        b.style.display = count === 0 ? 'none' : 'inline-flex'
      }
    }

    // lines
    const hosts = doc.querySelectorAll('[data-pg-cart-lines]')
    for (let h = 0; h < hosts.length; h++) {
      const host = hosts[h] as HTMLElement
      const tpl = host.querySelector('[data-pg-cart-row-template]') as HTMLElement | null
      const list = host.querySelector('[data-pg-cart-list]') as HTMLElement | null
      const empty = host.querySelector('[data-pg-cart-empty]') as HTMLElement | null
      if (!tpl || !list) continue
      tpl.style.display = 'none'
      list.innerHTML = ''
      for (let i = 0; i < lines.length; i++) {
        const l = lines[i]
        const key = keyOf(l)
        const row = tpl.cloneNode(true) as HTMLElement
        row.removeAttribute('data-pg-cart-row-template')
        row.setAttribute('data-pg-cart-row', key)
        // Set the layout, don't just clear `display: none`: the template
        // carries its display INLINE, so removing the property left the
        // row as a plain block and every cell stacked.
        row.style.display = 'flex'
        const img = row.querySelector('[data-pg-cart-image]') as HTMLImageElement | null
        if (img) {
          if (l.image) { img.src = l.image; img.alt = l.name; img.style.display = 'block' }
          else img.style.display = 'none'
        }
        const nameEl = row.querySelector('[data-pg-cart-name]') as HTMLElement | null
        if (nameEl) {
          nameEl.textContent = l.name
          if (l.href && nameEl.tagName === 'A') (nameEl as HTMLAnchorElement).href = l.href
        }
        // A booking line says WHEN. Older published pages have no dates slot
        // in their row template; the range then leads the second line.
        const range = l.start && l.end
          ? fmtDay(l.start) + ' → ' + fmtDay(l.end) + ' · ' + plural(Number(l.units) || 1, l.unit || 'night')
          : ''
        const datesEl = row.querySelector('[data-pg-cart-dates]') as HTMLElement | null
        if (datesEl) {
          if (range) { datesEl.textContent = range; datesEl.style.display = '' }
          else datesEl.style.display = 'none'
        }
        const metaEl = row.querySelector('[data-pg-cart-meta]') as HTMLElement | null
        if (metaEl) {
          const meta = !datesEl && range ? range + (l.meta ? ' · ' + l.meta : '') : (l.meta || '')
          if (meta) { metaEl.textContent = meta; metaEl.style.display = '' }
          else metaEl.style.display = 'none'
        }
        const unitEl = row.querySelector('[data-pg-cart-unit]') as HTMLElement | null
        if (unitEl) unitEl.textContent = money(l.price, l.prefix) + (range ? ' / ' + (l.unit || 'night') : ' each')
        const qtyEl = row.querySelector('[data-pg-cart-qty]') as HTMLElement | null
        if (qtyEl) qtyEl.textContent = String(l.qty)
        const lineEl = row.querySelector('[data-pg-cart-line-total]') as HTMLElement | null
        if (lineEl) lineEl.textContent = money(lineTotal(l), l.prefix)
        const steps = row.querySelectorAll('[data-pg-cart-step]')
        for (let k = 0; k < steps.length; k++) steps[k].setAttribute('data-pg-cart-sku', key)
        const rm = row.querySelector('[data-pg-cart-remove]')
        if (rm) rm.setAttribute('data-pg-cart-sku', key)
        list.appendChild(row)
      }
      if (empty) {
        // Same rule as the row: name the layout rather than clearing it.
        if (lines.length === 0) { empty.removeAttribute('hidden'); empty.style.display = 'flex' }
        else { empty.setAttribute('hidden', ''); empty.style.display = 'none' }
      }
    }

    // summaries
    const sums = doc.querySelectorAll('[data-pg-cart-summary]')
    for (let i = 0; i < sums.length; i++) {
      const box = sums[i] as HTMLElement
      const taxPct = num(box, 'data-pg-tax-percent', 0)
      const flat = num(box, 'data-pg-ship-flat', 0)
      const freeOver = box.getAttribute('data-pg-ship-free-over')
      const tax = taxPct ? Math.round(subtotal * (taxPct / 100)) : 0
      let ship = flat
      if (freeOver !== null && freeOver !== '' && subtotal >= Number(freeOver)) ship = 0
      if (count === 0) ship = 0
      const set = (sel: string, text: string) => {
        const el = box.querySelector(sel) as HTMLElement | null
        if (el) el.textContent = text
      }
      set('[data-pg-cart-subtotal]', money(subtotal, prefix))
      set('[data-pg-cart-tax]', money(tax, prefix))
      set('[data-pg-cart-shipping]', ship === 0 && flat
        ? (box.getAttribute('data-pg-free-label') || 'Free')
        : money(ship, prefix))
      set('[data-pg-cart-total]', money(subtotal + tax + ship, prefix))
      // Nothing to pay for: don't offer a checkout that cannot proceed.
      const go = box.querySelector('[data-pg-cart-checkout]') as HTMLElement | null
      if (go) {
        if (count === 0) { go.setAttribute('aria-disabled', 'true'); go.style.opacity = '0.5'; go.style.pointerEvents = 'none' }
        else { go.removeAttribute('aria-disabled'); go.style.removeProperty('opacity'); go.style.removeProperty('pointer-events') }
      }
    }

    // add buttons: say what is already in the basket. Summed by SKU so a
    // product booked for two date ranges still reads its whole quantity.
    const inCart: Record<string, number> = {}
    for (let i = 0; i < lines.length; i++) {
      const sku = String(lines[i].sku || '')
      if (sku) inCart[sku] = (inCart[sku] || 0) + (Number(lines[i].qty) || 0)
    }
    const adds = doc.querySelectorAll('[data-pg-add-cart]')
    for (let i = 0; i < adds.length; i++) {
      try { paintAdd(adds[i] as HTMLElement, inCart) } catch { /* one bad button never stops the rest */ }
    }

    // booking calendars
    const books = doc.querySelectorAll('[data-pg-cart-buy][data-pg-book]')
    for (let i = 0; i < books.length; i++) {
      try { renderBooking(books[i] as HTMLElement) } catch { /* one bad calendar never stops the rest */ }
    }
  }

  if (!(window as unknown as { __pgCartWired?: boolean }).__pgCartWired) {
    ;(window as unknown as { __pgCartWired?: boolean }).__pgCartWired = true

    const WATCHED = ['data-pg-add-cart', 'data-pg-cart-step', 'data-pg-cart-remove',
      'data-pg-buy-step', 'data-pg-cal-day', 'data-pg-cal-nav']
    doc.addEventListener('click', (e) => {
      let t = e.target as HTMLElement | null
      const hit = (n: HTMLElement) => {
        if (!n.hasAttribute) return false
        for (let i = 0; i < WATCHED.length; i++) if (n.hasAttribute(WATCHED[i])) return true
        return false
      }
      while (t && t !== doc.body && !hit(t)) t = t.parentElement
      if (!t || t === doc.body) return
      const box = t.closest ? t.closest('[data-pg-cart-buy]') as HTMLElement | null : null

      // ── calendar month
      if (t.hasAttribute('data-pg-cal-nav')) {
        e.preventDefault()
        if (!box) return
        const month = Number(box.getAttribute('data-pg-cal-month'))
        const d = new Date(month * 86400000)
        const delta = Number(t.getAttribute('data-pg-cal-nav')) || 0
        box.setAttribute('data-pg-cal-month',
          String(Math.floor(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + delta, 1) / 86400000)))
        renderBooking(box)
        return
      }

      // ── calendar day: first click is the start, the next one the end
      if (t.hasAttribute('data-pg-cal-day')) {
        e.preventDefault()
        if (!box) return
        const cfg = cfgOf(box)
        if (!cfg) return
        const d = dayNo(t.getAttribute('data-pg-cal-day') || '')
        const s = dayNo(box.getAttribute('data-pg-book-start') || '')
        const end = dayNo(box.getAttribute('data-pg-book-end') || '')
        const closing = isFinite(s) && !isFinite(end) && (cfg.unit === 'day' ? d >= s : d > s)
        if (closing) {
          const problem = rangeProblem(cfg, s, d)
          if (problem) {
            // The range does not work, but the day may: start a new stay
            // there instead of leaving the visitor stuck on a start date
            // that every later click is measured against.
            if (!startProblem(cfg, d)) {
              box.setAttribute('data-pg-book-start', isoOf(d))
              box.removeAttribute('data-pg-book-end')
              say(box, problem + ' Starting again from ' + fmtDay(isoOf(d)) + '.')
              renderBooking(box)
            } else {
              say(box, problem)
            }
            return
          }
          box.setAttribute('data-pg-book-end', isoOf(d))
        } else {
          const problem = startProblem(cfg, d)
          if (problem) { say(box, problem); return }
          box.setAttribute('data-pg-book-start', isoOf(d))
          box.removeAttribute('data-pg-book-end')
        }
        say(box, '')
        renderBooking(box)
        return
      }

      // ── quantity picker beside an Add button
      if (t.hasAttribute('data-pg-buy-step')) {
        e.preventDefault()
        const input = box && box.querySelector('[data-pg-buy-qty]') as HTMLInputElement | null
        if (!input) return
        const max = Math.max(1, Math.floor(Number(input.getAttribute('max')) || 9999))
        const next = (Math.floor(Number(input.value) || 1)) + (Number(t.getAttribute('data-pg-buy-step')) || 0)
        input.value = String(Math.max(1, Math.min(next, max)))
        if (box && box.hasAttribute('data-pg-book')) renderBooking(box)
        return
      }

      if (t.hasAttribute('data-pg-add-cart')) {
        e.preventDefault()
        let payload: Partial<CartLine> = {}
        try { payload = JSON.parse(t.getAttribute('data-pg-add-cart') || '{}') } catch { return }
        if (!payload.sku) return
        const add = pickedQty(box, Number(payload.qty) || 1)
        delete payload.start; delete payload.end; delete payload.units; delete payload.unit; delete payload.key
        if (box && box.hasAttribute('data-pg-book')) {
          const cfg = cfgOf(box)
          if (!cfg) return
          const s = dayNo(box.getAttribute('data-pg-book-start') || '')
          const end = dayNo(box.getAttribute('data-pg-book-end') || '')
          const problem = rangeProblem(cfg, s, end)
          if (problem) { say(box, problem); return }
          payload.start = isoOf(s)
          payload.end = isoOf(end)
          payload.units = unitsOf(cfg, s, end)
          payload.unit = cfg.unit
          say(box, '')
        }
        const key = keyOf(payload)
        const lines = read()
        let found = false
        for (let i = 0; i < lines.length; i++) {
          if (keyOf(lines[i]) === key) { lines[i].qty = Math.min((Number(lines[i].qty) || 0) + add, 9999); found = true; break }
        }
        if (!found) lines.push({ ...(payload as CartLine), key, qty: add })
        write(lines)
        // Say it landed: an Add button that looks inert is one people press
        // twice.
        //
        // Only the LABEL changes: the button also holds its icon, which
        // replacing the whole textContent used to delete. Older published
        // pages have no label span; they keep the old whole-button swap.
        const done = t.getAttribute('data-pg-added-label')
        const btn = t
        if (done && !btn.hasAttribute('data-pg-added')) {
          const labelEl = btn.querySelector('[data-pg-cart-label]') as HTMLElement | null
          if (labelEl && !btn.hasAttribute('data-pg-label')) btn.setAttribute('data-pg-label', labelEl.textContent || '')
          btn.setAttribute('data-pg-added', labelEl ? '' : (btn.textContent || ''))
          if (labelEl) labelEl.textContent = done
          else btn.textContent = done
          window.setTimeout(() => {
            const was = btn.getAttribute('data-pg-added')
            if (was === null) return
            btn.removeAttribute('data-pg-added')
            if (labelEl) labelEl.textContent = btn.getAttribute('data-pg-label') || ''
            else btn.textContent = was
            render()
          }, 1600)
        }
        const note = t.getAttribute('data-pg-cart-toast')
        if (note) {
          try { toast(t, JSON.parse(note) || {}) } catch { /* a notice never blocks the add */ }
        }
        render()
        return
      }

      const key = t.getAttribute('data-pg-cart-sku') || ''
      if (!key) return
      e.preventDefault()
      const lines = read()
      if (t.hasAttribute('data-pg-cart-remove')) {
        write(lines.filter((l) => keyOf(l) !== key))
      } else {
        const d = Number(t.getAttribute('data-pg-cart-step')) || 0
        const next: CartLine[] = []
        for (let i = 0; i < lines.length; i++) {
          if (keyOf(lines[i]) !== key) { next.push(lines[i]); continue }
          const q = (Number(lines[i].qty) || 0) + d
          if (q > 0) next.push({ ...lines[i], qty: Math.min(q, 9999) })
        }
        write(next)
      }
      render()
    })

    // Typing a quantity re-prices the booking summary beside it.
    doc.addEventListener('input', (e) => {
      const t = e.target as HTMLElement | null
      if (!t || !t.hasAttribute || !t.hasAttribute('data-pg-buy-qty')) return
      const box = t.closest ? t.closest('[data-pg-cart-buy]') as HTMLElement | null : null
      if (box && box.hasAttribute('data-pg-book')) renderBooking(box)
    })

    // Another tab, or another block on this page, changed the cart.
    window.addEventListener('storage', render)
    window.addEventListener('pg:cart', render)
  }

  render()
}

/** The same wiring, for a page with no React on it. */
export function cartScript(): string {
  return `(function(){
  var init=${initCart.toString()};
  function go(){try{init()}catch(e){}}
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',go);else go();
})();`
}
